<template>
  <div class="mission-timeline" :class="{ active, assigned: hasMission && ready, complete: step === 3 }" :aria-label="$t('配送进度：{0}', [$t(stageText)])" role="img">
    <div class="mission-timeline__track">
      <!-- SVG 虚线使用整数坐标，避免 Android WebView 将 1px 渐变线栅格化到半像素后丢失。 -->
      <svg class="mission-timeline__rail" viewBox="0 0 100 18" preserveAspectRatio="none">
        <line x1="0" y1="9" x2="100" y2="9"></line>
      </svg>
      <svg class="mission-timeline__progress" :style="{ width: `${trackProgress}%` }" viewBox="0 0 100 18" preserveAspectRatio="none">
        <line x1="0" y1="9" x2="100" y2="9"></line>
      </svg>
      <i v-for="point in points" :key="point" class="mission-timeline__point" :class="{ reached: isReached(point), ready: point === 0 && ready }" :style="{ left: `${point}%` }"></i>
      <span v-if="markerVisible" class="mission-timeline__marker" :style="{ left: `${markerPosition}%` }">
        <i class="mission-timeline__aura"></i>
        <i class="mission-timeline__core"></i>
      </span>
    </div>
    <div class="mission-timeline__labels"><span>{{ $t("准备") }}</span><span style="left: 25%">{{ $t("配送") }}</span><span :style="{ left: `${deliveryPosition}%` }">{{ $t("投放") }}</span><span>{{ $t("结束") }}</span></div>
  </div>
</template>

<script>
export default {
  name: 'GroundStationMissionTimeline',
  props: {
    active: { type: Boolean, default: false },
    ready: { type: Boolean, default: false },
    hasMission: { type: Boolean, default: false },
    deliveryProgress: { type: [Number, String], default: null },
    progress: { type: [Number, String], default: null },
    step: { type: Number, default: -1 },
    result: { type: String, default: '' }
  },
  methods: {
    isReached (point) {
      return (this.active && this.routePosition >= point) || this.step === 3 ||
        (point === 0 && this.ready) || (point === 25 && this.hasMission && this.ready)
    }
  },
  computed: {
    deliveryPoint () {
      const value = Number(this.deliveryProgress)
      return this.deliveryProgress !== null && this.deliveryProgress !== '' && Number.isFinite(value)
        ? Math.max(0, Math.min(100, value)) : 50
    },
    deliveryPosition () { return 25 + this.deliveryPoint * 0.75 },
    routePosition () { return 25 + this.displayProgress * 0.75 },
    markerVisible () { return this.active || (this.hasMission && this.ready) },
    markerPosition () { return this.active ? this.routePosition : 25 },
    trackProgress () { return this.step === 3 ? 100 : this.active ? this.routePosition : (this.hasMission && this.ready ? 25 : 0) },
    points () { return [0, 25, this.deliveryPosition, 100] },
    stageText () {
      if (this.result) return this.result
      if (this.hasMission && this.ready && this.step < 1) return '配送'
      return ['准备', '配送', '投放', '结束'][Math.max(0, Math.min(3, this.step))]
    },
    displayProgress () {
      const value = Number(this.progress)
      if (this.progress === null || this.progress === '' || !Number.isFinite(value)) return 0
      return Math.max(0, Math.min(100, value))
    }
  }
}
</script>

<style lang="scss" scoped>
.mission-timeline {
  height: 40px;
  margin: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.mission-timeline__track {
  width: 75%;
  margin: 0 auto;
  height: 18px;
  position: relative;
  flex: none;
}

.mission-timeline__labels { position: relative; width: 75%; margin: 0 auto; height: 16px; color: var(--ui-text-secondary); font-size: 11px; white-space: nowrap; }
.mission-timeline__labels span { position: absolute; top: 0; transform: translateX(-50%); }
.mission-timeline__labels span:first-child { left: 0; }
.mission-timeline__labels span:last-child { left: 100%; }

.mission-timeline__rail,
.mission-timeline__progress {
  height: 18px;
  position: absolute;
  top: 0;
  left: 0;
  overflow: visible;
}

.mission-timeline__rail line,
.mission-timeline__progress line {
  fill: none;
  stroke: var(--ui-text-secondary);
  stroke-width: 1;
  stroke-dasharray: 5 4;
  stroke-linecap: butt;
  vector-effect: non-scaling-stroke;
}

.mission-timeline__rail { width: 100%; opacity: .82; }
.mission-timeline__progress { transition: width var(--ui-duration-normal) var(--ui-ease-standard); }
.mission-timeline:not(.active):not(.assigned):not(.complete) .mission-timeline__progress { opacity: 0; transition: none; }
.mission-timeline__progress line { stroke: var(--ui-color-warning-emphasis); }

.mission-timeline__point {
  width: 8px;
  height: 8px;
  position: absolute;
  top: 50%;
  z-index: 2;
  border: 2px solid var(--ui-text-secondary);
  border-radius: 50%;
  background: var(--ui-bg-panel);
  transform: translate(-50%, -50%);
  transition: border-color .25s ease, background-color .25s ease, box-shadow .25s ease;
}

.mission-timeline__point.reached {
  background: var(--ui-color-warning-emphasis);
  box-shadow: 0 0 7px rgba(var(--ui-color-warning-emphasis-rgb), .34);
}

.mission-timeline__point.ready {
  background: var(--ui-color-warning-emphasis);
}

.mission-timeline__marker {
  width: 10px;
  height: 10px;
  position: absolute;
  top: 50%;
  z-index: 4;
  transform: translate(-50%, -50%);
  transition: left var(--ui-duration-normal) var(--ui-ease-standard);
}

.mission-timeline__core {
  position: absolute;
  inset: 2px;
  border-radius: 50%;
  background: var(--ui-color-warning);
  box-shadow: 0 0 6px rgba(var(--ui-color-warning-emphasis-rgb), .9);
}

.mission-timeline__aura {
  position: absolute;
  inset: -6px;
  border-radius: 50%;
  background: conic-gradient(from 0deg, transparent 0deg 30deg, rgba(var(--ui-color-warning-rgb), .95) 55deg, transparent 85deg 150deg, rgba(var(--ui-color-warning-rgb), .9) 180deg, transparent 215deg 285deg, rgba(var(--ui-color-warning-rgb), .9) 315deg, transparent 345deg);
  filter: drop-shadow(0 0 3px rgba(var(--ui-color-warning-rgb), .9));
  -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 3px), var(--ui-color-black) 0);
  mask: radial-gradient(farthest-side, transparent calc(100% - 3px), var(--ui-color-black) 0);
  animation: mission-aura-orbit 2.8s linear infinite, mission-aura-pulse 1.6s ease-in-out infinite;
}

@keyframes mission-aura-orbit {
  to { transform: rotate(360deg); }
}

@keyframes mission-aura-pulse {
  0%, 100% { opacity: .62; filter: drop-shadow(0 0 2px rgba(var(--ui-color-warning-rgb), .72)); }
  50% { opacity: .95; filter: drop-shadow(0 0 5px rgba(var(--ui-color-warning-rgb), .95)); }
}

@media (prefers-reduced-motion: reduce) {
  .mission-timeline__aura { animation: none; }
  .mission-timeline__marker, .mission-timeline__progress { transition: none; }
}
</style>
